import React from 'react';

const formatCurrency = (val) => `₹${parseFloat(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const SummaryTable = ({ stats }) => {
    const { salesStats, purchaseStats, salesPayStats, purchasePayStats, purchaseReturnStats, salesReturnStats, expenseStats } = stats;

    const rows = [
        { label: 'Purchase Invoices', data: purchaseStats, field: 'count', isCount: true },
        { label: 'Purchase Taxable Amount', data: purchaseStats, field: 'taxable' },
        { label: 'Purchase GST Amount', data: purchaseStats, field: 'gst' },
        { label: 'Purchase Invoice Amount', data: purchaseStats, field: 'total' },
        { label: 'Purchase Return Amount', data: purchaseReturnStats, field: 'total' },
        { label: 'Purchase Balance Amount', data: purchaseStats, field: 'total', payData: purchasePayStats, returnData: purchaseReturnStats },
        { label: 'Sales Invoices', data: salesStats, field: 'count', isCount: true },
        { label: 'Sales Taxable Amount', data: salesStats, field: 'taxable' },
        { label: 'Sales GST Amount', data: salesStats, field: 'gst' },
        { label: 'Sales Invoice Amount', data: salesStats, field: 'total' },
        { label: 'Sales Return Amount', data: salesReturnStats, field: 'total' },
        { label: 'Sales Payments Received', data: salesPayStats, field: 'total' },
        { label: 'Sale Balance Amount', data: salesStats, field: 'total', payData: salesPayStats, returnData: salesReturnStats },
        { label: 'Total Expenses', data: expenseStats, field: 'count', isCount: true },
        { label: 'Total Expense Amount', data: expenseStats, field: 'total' },
        { label: 'Expense Outstanding Amount', data: expenseStats, field: 'total' },
        { label: 'Profit / Loss Amount', custom: true }
    ];

    return (
        <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '14px' }}>
                <thead>
                    <tr style={{ background: '#f1f5f9', color: '#475569', fontWeight: '600' }}>
                        <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>SUMMARY</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>TODAY</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>CURRENT MONTH</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>LAST MONTH</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>OVERALL</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const renderCell = (period) => {
                            if (row.custom) {
                                const pl = salesStats[period].total - purchaseStats[period].total - expenseStats[period].total;
                                return formatCurrency(pl);
                            }
                            let val = row.data[period] ? row.data[period][row.field] : 0;
                            if (row.payData && row.payData[period]) {
                                val -= row.payData[period].total;
                            }
                            if (row.returnData && row.returnData[period]) {
                                val -= row.returnData[period].total;
                            }
                            return row.isCount ? val : formatCurrency(val);
                        };

                        return (
                            <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', color: '#1e293b', fontWeight: '500' }}>
                                <td style={{ padding: '16px', textAlign: 'left' }}>{row.label}</td>
                                <td style={{ padding: '16px' }}>{renderCell('today')}</td>
                                <td style={{ padding: '16px' }}>{renderCell('currentMonth')}</td>
                                <td style={{ padding: '16px' }}>{renderCell('lastMonth')}</td>
                                <td style={{ padding: '16px' }}>{renderCell('overall')}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export const GstSummaryTable = ({ stats }) => {
    const { salesStats, purchaseStats } = stats;

    const rows = [
        { label: 'Purchase GST Input Credit', field: 'gst', data: purchaseStats },
        { label: 'Sales GST Output Liability', field: 'gst', data: salesStats },
        { label: 'Purchase GST - Sale GST', custom: 'diff' },
        { label: 'Net GST Payable / Refundable', custom: 'net' },
        { label: 'CGST Total', custom: 'cgst' },
        { label: 'SGST Total', custom: 'sgst' },
        { label: 'IGST Total', custom: 'igst' },
    ];

    return (
        <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '14px' }}>
                <thead>
                    <tr style={{ background: '#f1f5f9', color: '#475569', fontWeight: '600' }}>
                        <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>GST SUMMARY</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>TODAY</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>CURRENT MONTH</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>LAST MONTH</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>OVERALL</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const renderCell = (period) => {
                            if (row.custom === 'diff') return formatCurrency(purchaseStats[period].gst - salesStats[period].gst);
                            if (row.custom === 'net') return formatCurrency(salesStats[period].gst - purchaseStats[period].gst);
                            if (row.custom === 'cgst') return formatCurrency(salesStats[period].cgst - purchaseStats[period].cgst);
                            if (row.custom === 'sgst') return formatCurrency(salesStats[period].sgst - purchaseStats[period].sgst);
                            if (row.custom === 'igst') return formatCurrency(salesStats[period].igst - purchaseStats[period].igst);
                            
                            return formatCurrency(row.data[period][row.field]);
                        };

                        return (
                            <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', color: '#1e293b', fontWeight: '500' }}>
                                <td style={{ padding: '16px', textAlign: 'left' }}>{row.label}</td>
                                <td style={{ padding: '16px' }}>{renderCell('today')}</td>
                                <td style={{ padding: '16px' }}>{renderCell('currentMonth')}</td>
                                <td style={{ padding: '16px' }}>{renderCell('lastMonth')}</td>
                                <td style={{ padding: '16px' }}>{renderCell('overall')}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
